import { z } from 'zod';

import {
  evaluateVisibilityCondition,
  getHiddenElementNames,
  parseVisibilityCondition,
} from './surveyVisibility';

/** Answerable element types the form schema builder and question renderers know how to handle. */
export const SUPPORTED_SURVEY_ELEMENT_TYPES = [
  'text',
  'comment',
  'dropdown',
  'radiogroup',
  'checkbox',
] as const;

/**
 * Field-name prefix for the acknowledgement that accompanies a Primum inquiry
 * question. The prefix exists so the acknowledgement can live in the same flat
 * form state as the answers without ever colliding with a question name, and it
 * is what lets the submitted payload be assembled from that state: a field
 * carrying it is folded into its question's answer object rather than sent as a
 * key of its own.
 */
export const SURVEY_CONSENT_FIELD_PREFIX = '__consent:';

export function getSurveyConsentFieldName(elementName: string) {
  return `${SURVEY_CONSENT_FIELD_PREFIX}${elementName}`;
}

/**
 * The choice value SurveyJS stores when a respondent picks the Other option, and
 * the suffix naming the field its free text goes in.
 *
 * Both are SurveyJS's own names rather than ours, and the API validates
 * submissions with survey-core itself, so a different value or suffix is not a
 * cosmetic difference — it fails validation server-side and returns a 400 this
 * client cannot explain to the respondent.
 */
export const SURVEY_OTHER_VALUE = 'other';

const SURVEY_OTHER_FIELD_SUFFIX = '-Comment';

const DEFAULT_OTHER_TEXT = 'Other';

export function getSurveyOtherFieldName(elementName: string) {
  return `${elementName}${SURVEY_OTHER_FIELD_SUFFIX}`;
}

/** Non-answerable element types the renderer displays but that never produce a form field. */
export const SURVEY_DISPLAY_ELEMENT_TYPES = ['html'] as const;

/** Page name for a schema authored as a flat `elements` array, which has no pages to name. */
const SYNTHETIC_PAGE_NAME = 'page1';

/**
 * Shared so the field builders and the conditional-visibility rule cannot drift.
 * Both raise the same requirement, one at the field level and one at the object
 * level, and a respondent must not be able to tell which layer answered them.
 */
const REQUIRED_FIELD_MESSAGE = 'This field is required';

const REQUIRED_SELECTION_MESSAGE = 'Select at least one option';

const surveyValidatorSchema = z
  .object({
    type: z.string(),
    minValue: z.number().optional(),
    maxValue: z.number().optional(),
  })
  .passthrough();

const MAX_GUIDANCE_TOPICS = 8;

const MAX_GUIDANCE_TIPS = 8;

const MAX_GUIDANCE_EXAMPLES = 3;

const guidanceText = z.string().trim().min(1);

const guidanceList = (max: number) =>
  z
    .array(guidanceText.catch(''))
    .transform((items) => items.filter(Boolean).slice(0, max))
    .optional()
    .catch(undefined);

const guidanceExamples = (detailKey: 'note' | 'reason') =>
  z
    .array(
      z
        .object({
          text: guidanceText.optional().catch(undefined),
          [detailKey]: guidanceText.optional().catch(undefined),
        })
        .catch({})
    )
    .transform((items) =>
      items
        .filter((item): item is typeof item & { text: string } => Boolean(item.text))
        .slice(0, MAX_GUIDANCE_EXAMPLES)
    )
    .optional()
    .catch(undefined);

/**
 * Presentation copy for the "ask an expert" screen. Every field is optional and
 * caught, and the whole block is caught too, so malformed guidance degrades to
 * the plain question rather than dropping it. A block left with nothing in it
 * collapses to `undefined` so it cannot switch on the guided styling alone.
 */
const inquiryGuidanceSchema = z
  .object({
    stepLabel: guidanceText.optional().catch(undefined),
    subtitle: guidanceText.optional().catch(undefined),
    topics: guidanceList(MAX_GUIDANCE_TOPICS),
    goodExamples: guidanceExamples('note'),
    lessHelpfulExamples: guidanceExamples('reason'),
    placeholder: guidanceText.optional().catch(undefined),
    contextTips: guidanceList(MAX_GUIDANCE_TIPS),
  })
  .transform((guidance) =>
    Object.values(guidance).some((value) => (Array.isArray(value) ? value.length : value))
      ? guidance
      : undefined
  )
  .optional()
  .catch(undefined);

/**
 * Marks a `comment` element whose answer the backend turns into a real Primum
 * inquiry.
 *
 * Every field here has to fail *open*. An element that fails to parse is
 * dropped entirely, which would delete the question from the survey instead of
 * rendering it — the exact silent-omission failure this element type exists to
 * fix. So `enabled` catches rather than rejects a non-boolean, leaving the
 * strict `=== true` gate to the predicate below, and unrecognized fields pass
 * through untouched. The marker also carries a `cancerTypeId` that the server
 * accepts as a number *or* a numeric string and that the client never reads,
 * since routing is entirely server-side; typing it at all would only create
 * another way to lose the question.
 */
const primumInquiryMarkerSchema = z
  .object({
    enabled: z.boolean().optional().catch(undefined),
    guidance: inquiryGuidanceSchema,
  })
  .passthrough();

export type InquiryGuidance = NonNullable<z.infer<typeof inquiryGuidanceSchema>>;

const surveyElementSchema = z
  .object({
    name: z.string(),
    type: z.string(),
    // Display elements like `html` carry no title in survey-core's schema.
    title: z.string().optional(),
    isRequired: z.boolean().optional(),
    validators: z.array(surveyValidatorSchema).optional(),
    choices: z.array(z.string()).optional(),
    choicesOrder: z.enum(['none', 'random']).optional().catch(undefined),
    html: z.string().optional(),
    // Presentation-only, and caught rather than rejected for the same
    // fail-open reason as the inquiry marker's fields: a survey authored with
    // a stringified row count must still render its question.
    rows: z.number().optional().catch(undefined),
    // Which on-screen keyboard a `text` question asks for. Presentation-only on
    // the server too: survey-core validates a `number` question's answer no
    // differently from a `text` one, so this can never be the reason a
    // submission is rejected.
    inputType: z.string().optional().catch(undefined),
    showOtherItem: z.boolean().optional().catch(undefined),
    otherText: z.string().optional().catch(undefined),
    minSelectedChoices: z.number().optional().catch(undefined),
    maxSelectedChoices: z.number().optional().catch(undefined),
    // Caught rather than rejected for the same fail-open reason, and the stakes
    // are higher here than for presentation: a rejected element is dropped from
    // the survey, so a malformed condition would delete the very question it
    // was meant to reveal.
    visibleIf: z.string().optional().catch(undefined),
    primumInquiry: primumInquiryMarkerSchema.optional(),
  })
  .refine(
    (element) =>
      !SUPPORTED_SURVEY_ELEMENT_TYPES.includes(
        element.type as (typeof SUPPORTED_SURVEY_ELEMENT_TYPES)[number]
      ) || typeof element.title === 'string',
    { message: 'title is required for answerable element types', path: ['title'] }
  );

export type SurveyElement = z.infer<typeof surveyElementSchema>;

/**
 * The backend recognizes an inquiry question only as a `comment` element whose
 * marker is explicitly `enabled: true`. Both halves of that test have to stay
 * in lockstep with the server, or the client renders an acknowledgement for a
 * question that produces no inquiry, or omits one for a question that does.
 */
export function isPrimumInquiryElement(element: SurveyElement) {
  return element.type === 'comment' && element.primumInquiry?.enabled === true;
}

/**
 * The selection floor a checkbox question imposes, or `undefined` for none.
 *
 * Measured against survey-core, which the API validates with: this fires even
 * when the question is *not* required and even when no answer is sent at all.
 * A floor therefore makes an otherwise optional question answerable-at-N rather
 * than merely capping it, and a client that ignored it would hand the
 * respondent a 400 phrased in survey-core's words for a rule it never showed
 * them.
 */
export function getMinSelectedChoices(element: SurveyElement) {
  return element.type === 'checkbox' && typeof element.minSelectedChoices === 'number'
    ? element.minSelectedChoices
    : undefined;
}

/**
 * The selection cap a checkbox question imposes, or `undefined` for none.
 *
 * Deliberately *not* validated. survey-core accepts a payload over the cap, so
 * raising an error here would reject something the server would have stored —
 * the client inventing a rule rather than reflecting one. It is applied as a UI
 * limit instead, the same way SurveyJS's own renderer applies it, which cannot
 * produce a submission the server disagrees with.
 */
export function getMaxSelectedChoices(element: SurveyElement) {
  return element.type === 'checkbox' && typeof element.maxSelectedChoices === 'number'
    ? element.maxSelectedChoices
    : undefined;
}

/** Choice types that can carry an Other option. A free-text question cannot. */
const OTHER_CAPABLE_ELEMENT_TYPES = ['radiogroup', 'dropdown', 'checkbox'];

export function hasOtherItem(element: SurveyElement) {
  return element.showOtherItem === true && OTHER_CAPABLE_ELEMENT_TYPES.includes(element.type);
}

export function getSurveyOtherText(element: SurveyElement) {
  return element.otherText ?? DEFAULT_OTHER_TEXT;
}

/**
 * Whether an answer currently selects Other, across both the single-choice
 * shape (a bare string) and the checkbox shape (an array of strings).
 *
 * Shared so the renderers, the validation rule, and the payload builder cannot
 * disagree about when the free-text field applies — a divergence would either
 * demand text for a question that does not show the input, or submit text the
 * respondent has already deselected.
 */
export function isOtherSelected(value: unknown) {
  return Array.isArray(value) ? value.includes(SURVEY_OTHER_VALUE) : value === SURVEY_OTHER_VALUE;
}

/**
 * Whether an element's visibility depends on other answers.
 *
 * Keyed on the presence of a condition rather than on whether that condition
 * parses, and both the schema builder and the visibility rule must key on it
 * identically: the builder relaxes exactly these fields to optional and the rule
 * clears exactly these fields, so a divergence would clear a field that is still
 * required and produce a form nobody can submit. An unparseable condition is
 * relaxed here and then never lands in the hidden set, so it stays required.
 *
 * Exported so the renderer tests the same thing rather than restating it — the
 * invariant above is only worth anything if there is one predicate to keep.
 */
export function isConditionallyVisible(element: SurveyElement) {
  return typeof element.visibleIf === 'string';
}

/**
 * Resolves the hidden set for a survey's elements.
 *
 * Wraps the generic resolver with the set of elements that can actually hold an
 * answer, so a condition reading a display block or a question type this client
 * cannot render fails open to visible rather than resolving against a value that
 * will never arrive. Every caller — validation, the payload builder, and the
 * renderer — has to go through here, or they would disagree about what is hidden
 * and the form would validate one shape while submitting another.
 */
export function getHiddenSurveyElementNames(
  elements: SurveyElement[],
  values: Record<string, unknown>
) {
  const answerableNames = new Set(
    elements
      .filter((element) =>
        SUPPORTED_SURVEY_ELEMENT_TYPES.includes(
          element.type as (typeof SUPPORTED_SURVEY_ELEMENT_TYPES)[number]
        )
      )
      .map((element) => element.name)
  );

  return getHiddenElementNames(elements, values, answerableNames);
}

/**
 * One page of a survey, as the stepper lays it out.
 *
 * Structurally a `VisibilityRule`, so the unsupported-condition reporter can be
 * pointed straight at a page list without a shim.
 */
export type SurveyPage = {
  name: string;
  title?: string;
  visibleIf?: string;
  elements: SurveyElement[];
};

function parseElementArray(elements: unknown): SurveyElement[] {
  if (!Array.isArray(elements)) {
    return [];
  }

  return elements
    .map((element) => surveyElementSchema.safeParse(element))
    .filter((result) => result.success)
    .map((result) => result.data);
}

/**
 * Folds a page's condition into one of its elements, so that a question on a
 * hidden page is hidden by the same mechanism as any other conditional
 * question.
 *
 * This is what keeps pages from becoming a second visibility concept. Every
 * existing rule — the hidden-name resolver, the required-when-visible check,
 * and the hidden-answer stripping in the payload — is keyed on elements, so a
 * folded condition makes all three treat a hidden page correctly with no
 * page-aware code in any of them.
 *
 * An unparseable page condition is deliberately *not* folded. Combining it
 * would make the element's whole condition unparseable, and while failing open
 * is the right answer for the page, it would also discard the element's own
 * valid condition and show a question that should still be gated. Left
 * unfolded, the page fails open on its own and the element keeps its rule.
 */
function foldPageCondition(
  pageVisibleIf: string | undefined,
  elementVisibleIf: string | undefined
) {
  if (typeof pageVisibleIf !== 'string' || parseVisibilityCondition(pageVisibleIf) === undefined) {
    return elementVisibleIf;
  }

  if (typeof elementVisibleIf !== 'string') {
    return pageVisibleIf;
  }

  return `(${pageVisibleIf}) and (${elementVisibleIf})`;
}

/**
 * `SurveyDefinition.schema` is server-authored and typed as `unknown` on the
 * wire — this is the client's sole gate on shape. An element missing the
 * baseline `name`/`type` fields is dropped rather than surfacing a malformed
 * field; an element with an unrecognized `type` is kept here and filtered
 * later by the form-schema builder and renderer, per the
 * unrecognized-type-is-skipped-not-fatal requirement.
 *
 * A schema carrying a top-level `elements` array rather than `pages` becomes a
 * single synthetic page, so a flat survey renders exactly as it did before
 * pages existed — one page is the case with no stepper.
 */
export function parseSurveyPages(schema: unknown): SurveyPage[] {
  if (typeof schema !== 'object' || schema === null) {
    return [];
  }

  if ('pages' in schema) {
    const { pages } = schema as { pages: unknown };

    if (!Array.isArray(pages)) {
      return [];
    }

    return pages.flatMap((page, index) => {
      if (typeof page !== 'object' || page === null) {
        return [];
      }

      const { name, title, visibleIf, elements } = page as Record<string, unknown>;

      const pageVisibleIf = typeof visibleIf === 'string' ? visibleIf : undefined;

      return [
        {
          name: typeof name === 'string' && name !== '' ? name : `page${index + 1}`,
          title: typeof title === 'string' ? title : undefined,
          visibleIf: pageVisibleIf,
          elements: parseElementArray(elements).map((element) => ({
            ...element,
            visibleIf: foldPageCondition(pageVisibleIf, element.visibleIf),
          })),
        },
      ];
    });
  }

  if (!('elements' in schema)) {
    return [];
  }

  const elements = parseElementArray((schema as { elements: unknown }).elements);

  return elements.length > 0 ? [{ name: SYNTHETIC_PAGE_NAME, elements }] : [];
}

/**
 * The message shown once a response has been accepted.
 *
 * This is the *only* place a survey should say thank you. An authored
 * thank-you page is the last thing a respondent reads while their answers are
 * still unsent, so it reads as the end of the task and they leave without
 * submitting — which is why authors end up appending a reminder to press the
 * button. A completion message cannot be reached without submitting, so the
 * problem cannot arise.
 *
 * Field names are survey-core's own, so the same schema produces the same
 * message wherever it is rendered and the admin tooling can adopt it without a
 * Primum-specific concept.
 */
export type SurveyCompletion = {
  html?: string;
  /** Checked in order; the first match wins, matching survey-core. */
  conditions: { expression: string; html: string }[];
};

export function parseSurveyCompletion(schema: unknown): SurveyCompletion {
  if (typeof schema !== 'object' || schema === null) {
    return { conditions: [] };
  }

  const { completedHtml, completedHtmlOnCondition } = schema as {
    completedHtml?: unknown;
    completedHtmlOnCondition?: unknown;
  };

  const conditions = Array.isArray(completedHtmlOnCondition)
    ? completedHtmlOnCondition.flatMap((entry) => {
        if (typeof entry !== 'object' || entry === null) {
          return [];
        }

        const { expression, html } = entry as { expression?: unknown; html?: unknown };

        return typeof expression === 'string' && typeof html === 'string'
          ? [{ expression, html }]
          : [];
      })
    : [];

  return {
    html: typeof completedHtml === 'string' ? completedHtml : undefined,
    conditions,
  };
}

/**
 * Picks the message for one submitted response.
 *
 * A condition that cannot be parsed or evaluated is skipped rather than
 * matched, which is the opposite of the fail-open rule used for question
 * visibility — and deliberately so. Failing open there shows an extra
 * question, which is recoverable; failing open here would tell a respondent
 * they were screened out, or thank them for the wrong thing, on the strength
 * of an expression nobody could read. The default message is the safe answer.
 *
 * Evaluated against the submitted payload, so a condition reads exactly what
 * was stored. The one shape to avoid is a condition on a Primum inquiry
 * question: those reach the payload as an object rather than their text, and
 * an object satisfies `notempty` whether or not anything was written.
 */
export function resolveSurveyCompletionHtml(
  completion: SurveyCompletion,
  answers: Record<string, unknown>
) {
  for (const { expression, html } of completion.conditions) {
    const condition = parseVisibilityCondition(expression);

    if (condition && evaluateVisibilityCondition(condition, answers)) {
      return html;
    }
  }

  return completion.html;
}

/**
 * The flat element list. Validation, the payload builder, and the draft all
 * work on every question at once regardless of layout; only the renderer cares
 * which page a question sits on.
 */
export function parseSurveyElements(schema: unknown): SurveyElement[] {
  return parseSurveyPages(schema).flatMap((page) => page.elements);
}

/**
 * RHF/Input values arrive as strings. Blank strings need to become
 * `undefined` so a field's own required/optional check applies, rather than
 * failing whatever format validation (numeric, email, ...) runs after it.
 */
function blankToUndefined(value: unknown) {
  return typeof value !== 'string' || value.trim() === '' ? undefined : value;
}

function buildNumericFieldSchema(
  validator: { minValue?: number; maxValue?: number },
  isRequired: boolean
) {
  let numberSchema = z.number({
    required_error: REQUIRED_FIELD_MESSAGE,
    invalid_type_error: 'Enter a number',
  });

  if (typeof validator.minValue === 'number') {
    numberSchema = numberSchema.min(validator.minValue, `Must be at least ${validator.minValue}`);
  }

  if (typeof validator.maxValue === 'number') {
    numberSchema = numberSchema.max(validator.maxValue, `Must be at most ${validator.maxValue}`);
  }

  const finalNumberSchema = isRequired ? numberSchema : numberSchema.optional();

  // Numeric-looking strings still need converting to numbers here, but
  // non-numeric strings are left alone so z.number() reports them as invalid
  // instead of silently coercing garbage input to 0/NaN.
  return z.preprocess((value) => {
    const blankHandled = blankToUndefined(value);

    if (typeof blankHandled !== 'string') {
      return blankHandled;
    }

    const parsed = Number(blankHandled);

    return Number.isNaN(parsed) ? blankHandled : parsed;
  }, finalNumberSchema);
}

function buildTextFieldSchema(isRequired: boolean) {
  return isRequired
    ? z.string({ required_error: REQUIRED_FIELD_MESSAGE }).trim().min(1, REQUIRED_FIELD_MESSAGE)
    : z.string().trim().optional();
}

function buildEmailFieldSchema(isRequired: boolean) {
  const emailSchema = z
    .string({ required_error: REQUIRED_FIELD_MESSAGE })
    .trim()
    .email('Enter a valid email address');

  const finalEmailSchema = isRequired ? emailSchema : emailSchema.optional();

  return z.preprocess(blankToUndefined, finalEmailSchema);
}

function buildChoiceFieldSchema(isRequired: boolean) {
  return isRequired
    ? z.string({ required_error: REQUIRED_FIELD_MESSAGE }).min(1, REQUIRED_FIELD_MESSAGE)
    : z.string().optional();
}

function buildMultiChoiceFieldSchema(isRequired: boolean) {
  return isRequired
    ? z.array(z.string()).min(1, REQUIRED_SELECTION_MESSAGE)
    : z.array(z.string()).optional();
}

/**
 * The Other free text is registered optional here and required by
 * `withOtherItemRule`, because whether it applies depends on an answer this
 * shape is fixed against — the same split the conditional-visibility builders
 * make, for the same reason.
 */
function buildOtherFieldSchema() {
  return z.string().trim().optional();
}

/**
 * Builds a Zod object schema from a survey's parsed elements, following the
 * codebase's `useForm` + `zodResolver` convention. An element whose `type`
 * isn't in `SUPPORTED_SURVEY_ELEMENT_TYPES` is skipped so the schema (and
 * therefore the rendered form) silently omits questions this client doesn't
 * know how to render yet, rather than failing the whole survey.
 *
 * A conditional element is built as optional here whatever its `isRequired`
 * says, because this shape is fixed for the life of the form while the answers
 * that decide visibility are not. Its required-ness is reapplied at validation
 * time by the conditional-visibility wrapper, which is the only layer that sees
 * the answers — build one without the other and a hidden question either blocks
 * submission forever or never demands an answer at all.
 */
export function buildSurveyFormSchema(elements: SurveyElement[]) {
  const shape: Record<string, z.ZodTypeAny> = {};

  for (const element of elements) {
    if (
      !SUPPORTED_SURVEY_ELEMENT_TYPES.includes(
        element.type as (typeof SUPPORTED_SURVEY_ELEMENT_TYPES)[number]
      )
    ) {
      continue;
    }

    const isRequired = (element.isRequired ?? false) && !isConditionallyVisible(element);

    if (hasOtherItem(element)) {
      shape[getSurveyOtherFieldName(element.name)] = buildOtherFieldSchema();
    }

    if (element.type === 'checkbox') {
      shape[element.name] = buildMultiChoiceFieldSchema(isRequired);

      continue;
    }

    if (element.type === 'dropdown' || element.type === 'radiogroup') {
      shape[element.name] = buildChoiceFieldSchema(isRequired);

      continue;
    }

    if (element.type === 'comment') {
      shape[element.name] = buildTextFieldSchema(isRequired);

      if (isPrimumInquiryElement(element)) {
        shape[getSurveyConsentFieldName(element.name)] = z.boolean();
      }

      continue;
    }

    const numericValidator = element.validators?.find((validator) => validator.type === 'numeric');

    const emailValidator = element.validators?.find((validator) => validator.type === 'email');

    if (numericValidator) {
      shape[element.name] = buildNumericFieldSchema(numericValidator, isRequired);
    } else if (emailValidator) {
      shape[element.name] = buildEmailFieldSchema(isRequired);
    } else {
      shape[element.name] = buildTextFieldSchema(isRequired);
    }
  }

  return z.object(shape);
}

function isEmptyAnswer(value: unknown) {
  if (value === null || value === undefined) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return typeof value === 'string' && value.trim() === '';
}

/** Matches the server's own test for whether an answer creates an inquiry. */
function hasInquiryText(value: unknown) {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Requires the no-PII/Terms acknowledgement on a Primum inquiry question, but
 * only once the respondent has actually written something.
 *
 * An inquiry question is often `isRequired: false` — the survey invites a
 * clinical question rather than demanding one — so an unconditionally required
 * acknowledgement would block the whole survey for someone who never wanted to
 * ask anything. The server creates an inquiry only for a non-blank, trimmed
 * answer, so gating on the same test demands the acknowledgement exactly when
 * an inquiry will really be created.
 *
 * A hidden inquiry question needs no special case here, but only because the
 * conditional-visibility rule wraps *inside* this one and has already cleared
 * the hidden answer by the time this runs. Keep that nesting order: reversed,
 * this would demand an acknowledgement for a question nobody can see.
 *
 * Applied as a wrapper rather than inside `buildSurveyFormSchema` because a
 * refinement returns a `ZodEffects`, which has no `.shape`. A survey with no
 * inquiry question is handed back untouched so it keeps the plain object type.
 */
export function withInquiryConsentRule<Schema extends z.ZodTypeAny>(
  schema: Schema,
  elements: SurveyElement[]
) {
  const inquiryElements = elements.filter(isPrimumInquiryElement);

  if (inquiryElements.length === 0) {
    return schema;
  }

  return schema.superRefine((values, ctx) => {
    const answers = values as Record<string, unknown>;

    for (const element of inquiryElements) {
      const consentFieldName = getSurveyConsentFieldName(element.name);

      if (hasInquiryText(answers[element.name]) && answers[consentFieldName] !== true) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [consentFieldName],
          message: 'Please confirm before submitting',
        });
      }
    }
  });
}

/**
 * Discards the answers to questions the respondent cannot currently see.
 *
 * React Hook Form keeps a field's value after it stops being rendered, so a
 * hidden question's answer still reaches validation. Left alone it does real
 * damage: a hidden numeric question holding text fails its own type check, and
 * Zod aborts the whole object before any refinement runs — the form then refuses
 * to submit while showing no error at all, on a question nobody can see.
 * Clearing the values is what exempts a hidden question from every check its
 * field carries, not merely from being required.
 */
function clearHiddenAnswers(values: unknown, elements: SurveyElement[]) {
  if (typeof values !== 'object' || values === null) {
    return values;
  }

  const answers = values as Record<string, unknown>;

  const hiddenNames = getHiddenSurveyElementNames(elements, answers);

  if (hiddenNames.size === 0) {
    return answers;
  }

  const remainingAnswers = { ...answers };

  hiddenNames.forEach((name) => delete remainingAnswers[name]);

  return remainingAnswers;
}

/**
 * Makes a conditional question behave as though it were only ever asked when its
 * condition holds: exempt from validation while hidden, and subject to its own
 * `isRequired` the moment it is shown.
 *
 * The two halves have to run at different stages, which is why this is a
 * preprocess wrapping a refinement rather than one refinement. Clearing must
 * happen *before* the object parses, since an aborted field short-circuits every
 * refinement after it; re-imposing required-ness must happen *after*, because
 * that is the first point where the answers and the schema are in the same
 * place. Rebuilding the schema per keystroke would do the same job, but tying
 * validity to render state rather than to the values being validated invites the
 * submitted payload and the validated payload to disagree.
 *
 * The consent field is deliberately never cleared: it is a non-optional boolean,
 * so removing it would fail a field the respondent cannot see. It needs no
 * clearing — the payload builder strips it, and clearing the inquiry answer it
 * accompanies is already enough to stop the consent rule firing.
 *
 * A survey with no conditional question is handed back untouched, for the same
 * reason the inquiry rule does so.
 */
export function withVisibilityRules<Schema extends z.ZodTypeAny>(
  schema: Schema,
  elements: SurveyElement[]
) {
  const conditionalElements = elements.filter(isConditionallyVisible);

  if (conditionalElements.length === 0) {
    return schema;
  }

  const requiredElements = conditionalElements.filter((element) => element.isRequired === true);

  const schemaWithRequired = schema.superRefine((values, ctx) => {
    const answers = values as Record<string, unknown>;

    const hiddenNames = getHiddenSurveyElementNames(elements, answers);

    for (const element of requiredElements) {
      if (hiddenNames.has(element.name) || !isEmptyAnswer(answers[element.name])) {
        continue;
      }

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [element.name],
        message: element.type === 'checkbox' ? REQUIRED_SELECTION_MESSAGE : REQUIRED_FIELD_MESSAGE,
      });
    }
  });

  return z.preprocess((values) => clearHiddenAnswers(values, elements), schemaWithRequired);
}

function selectionFloorMessage(minimum: number) {
  return minimum === 1 ? REQUIRED_SELECTION_MESSAGE : `Select at least ${minimum} options`;
}

/**
 * Enforces each checkbox question's `minSelectedChoices`.
 *
 * Kept out of the field schema and applied here for the same reason the Other
 * rule is: composed outside the visibility wrapper, it sees answers whose
 * hidden entries have already been cleared, so a floor never applies to a
 * question the respondent cannot see. Inside the field schema it would also
 * have to be relaxed and re-imposed for every conditional question, which is
 * the machinery `withVisibilityRules` already owns.
 */
export function withSelectionCountRule<Schema extends z.ZodTypeAny>(
  schema: Schema,
  elements: SurveyElement[]
) {
  const flooredElements = elements.filter((element) => (getMinSelectedChoices(element) ?? 0) > 0);

  if (flooredElements.length === 0) {
    return schema;
  }

  return schema.superRefine((values, ctx) => {
    const answers = values as Record<string, unknown>;

    for (const element of flooredElements) {
      const minimum = getMinSelectedChoices(element) ?? 0;

      const answer = answers[element.name];

      // An absent answer is only exempt when the question was cleared for being
      // hidden; a visible question left untouched is short of its floor, which
      // is how survey-core reads it too.
      if (!(element.name in answers)) {
        continue;
      }

      if (Array.isArray(answer) && answer.length >= minimum) {
        continue;
      }

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [element.name],
        message: selectionFloorMessage(minimum),
      });
    }
  });
}

/**
 * Requires the Other free text whenever Other is the selected answer.
 *
 * This is not a stricter client rule than the server's — it is the server's
 * rule. survey-core rejects a submission that selects Other with no comment
 * regardless of whether the question is `isRequired`, so without this the
 * respondent gets a 400 carrying survey-core's own wording for a field this
 * client never told them to fill in.
 *
 * Composed *outside* `withVisibilityRules`, which is what makes a hidden
 * question need no special case here: the hidden answer has already been
 * cleared by the time this runs, so Other reads as unselected and no text is
 * demanded for a question nobody can see. Keep that nesting, for the same
 * reason the inquiry-consent rule documents.
 */
export function withOtherItemRule<Schema extends z.ZodTypeAny>(
  schema: Schema,
  elements: SurveyElement[]
) {
  const otherElements = elements.filter(hasOtherItem);

  if (otherElements.length === 0) {
    return schema;
  }

  return schema.superRefine((values, ctx) => {
    const answers = values as Record<string, unknown>;

    for (const element of otherElements) {
      const otherFieldName = getSurveyOtherFieldName(element.name);

      if (isOtherSelected(answers[element.name]) && isEmptyAnswer(answers[otherFieldName])) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [otherFieldName],
          message: REQUIRED_FIELD_MESSAGE,
        });
      }
    }
  });
}

/**
 * One Primum inquiry question's answer as the API takes it.
 *
 * Field names deliberately match the inquiry submission body, because the
 * server passes them through to inquiry submission untranslated. The expert
 * choice that body also accepts is omitted: this client offers no expert
 * picker, and an absent `expertId` is what routes the inquiry to Primum for
 * triage rather than at someone in particular.
 */
export type SurveyInquiryAnswer = {
  message: string;
  /** The respondent's own attestation. Never inferred, so never defaulted true. */
  noPII: boolean;
};

/**
 * Turns the form's flat field values into the `answers` payload the survey API
 * stores, folding each Primum inquiry question's acknowledgement into that
 * question's own answer.
 *
 * The acknowledgement has to travel with the answer rather than beside it: the
 * server reads the attestation off the inquiry answer object and defaults it to
 * false, so an answer sent as bare text submits the resulting clinical inquiry
 * as though the respondent attested to nothing — which is exactly the checkbox
 * they were just made to tick.
 *
 * An inquiry question keeps the object shape even when it was left blank, so a
 * question's stored answers do not change shape depending on whether someone
 * filled it in. A blank message creates no inquiry, the same as sending nothing.
 *
 * A question hidden by its own condition is omitted entirely, so the stored
 * response says nothing about a question the respondent was never shown. This
 * is also what lets the form keep a hidden question's value rather than
 * clearing it: someone who toggles an answer back finds what they typed still
 * there, and the value that was never meant to count is dropped here instead.
 */
export function buildSurveyAnswers(
  values: Record<string, unknown>,
  elements: SurveyElement[]
): Record<string, unknown> {
  const inquiryNames = new Set(
    elements.filter(isPrimumInquiryElement).map((element) => element.name)
  );

  const hiddenNames = getHiddenSurveyElementNames(elements, values);

  /**
   * Other free text that no longer belongs to a selected Other option. The
   * hidden-name set holds element names only, so this field needs resolving on
   * its own — otherwise someone who types an Other value and then picks a real
   * choice submits text for an option they are not choosing.
   */
  const droppedOtherFieldNames = new Set(
    elements
      .filter(
        (element) =>
          hasOtherItem(element) &&
          (hiddenNames.has(element.name) || !isOtherSelected(values[element.name]))
      )
      .map((element) => getSurveyOtherFieldName(element.name))
  );

  return Object.fromEntries(
    Object.entries(values)
      .filter(
        ([key]) =>
          !key.startsWith(SURVEY_CONSENT_FIELD_PREFIX) &&
          !hiddenNames.has(key) &&
          !droppedOtherFieldNames.has(key)
      )
      .map(([key, value]) => {
        if (!inquiryNames.has(key)) {
          return [key, value];
        }

        const answer: SurveyInquiryAnswer = {
          message: typeof value === 'string' ? value : '',
          noPII: values[getSurveyConsentFieldName(key)] === true,
        };

        return [key, answer];
      })
  );
}
